import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Type, 
  Image, 
  Square, 
  Columns, 
  Link, 
  Code, 
  Save,
  Eye,
  Smartphone,
  Monitor,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link2,
  ImageIcon,
  Trash2,
  Copy,
  Move,
  Settings,
  ChevronDown,
  ChevronUp,
  Plus,
  Palette,
  Layout,
  Mail,
  Calendar,
  Gift,
  Star,
  Heart,
  ShoppingBag,
  Megaphone,
  FileText,
  Users,
  Images,
  Upload,
  GripVertical
} from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import StockPhotoBrowser from './StockPhotoBrowser';
import UploadedPhotosBrowser from './UploadedPhotosBrowser';
// Use Supabase Storage for better performance
import { uploadedImageService } from '../services/uploadedImageService';
// Fallback: Use local storage if bucket not available
// import { uploadedImageService } from '../services/uploadedImageServiceLocal';
import { useAuth } from '../hooks/useAuth';

interface EmailTemplateBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: any) => void;
  initialTemplate?: any;
}

interface UploadedImage {
  id: string;
  url: string;
  name: string;
  uploadedAt: Date;
}

// Pre-designed templates
const emailTemplates = [
  {
    id: 'welcome',
    name: 'Welcome Email',
    icon: Heart,
    category: 'Onboarding',
    preview: 'A warm welcome message for new customers',
    content: {
      subject: 'Welcome to {{business_name}}!',
      preheader: 'We\'re excited to have you with us',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#6366f1',
            padding: '40px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'Welcome Aboard!',
            subheading: 'We\'re thrilled to have you join our community',
            image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800',
            buttonText: 'Get Started',
            buttonUrl: '#'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Thank you for choosing {{business_name}}. We\'re committed to providing you with exceptional service.',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'booking-confirmation',
    name: 'Booking Confirmation',
    icon: Calendar,
    category: 'Bookings',
    preview: 'Confirm customer bookings with style',
    content: {
      subject: 'Booking Confirmed - {{booking_date}}',
      preheader: 'Your booking has been confirmed',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#10b981',
            padding: '30px'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Hi {{customer_name}},\n\nYour booking has been confirmed!',
            align: 'left'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Booking Details',
            items: [
              'Date: {{booking_date}}',
              'Time: {{booking_time}}',
              'Service: {{service_name}}',
              'Location: {{location}}'
            ]
          }
        }
      ]
    }
  },
  {
    id: 'promotion',
    name: 'Promotional Offer',
    icon: Gift,
    category: 'Marketing',
    preview: 'Eye-catching promotional email',
    content: {
      subject: 'Special Offer - {{discount}}% Off!',
      preheader: 'Limited time offer just for you',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#ef4444',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: '{{discount}}% OFF',
            subheading: 'Limited Time Offer',
            buttonText: 'Shop Now',
            buttonUrl: '#'
          }
        }
      ]
    }
  },
  {
    id: 'reminder',
    name: 'Service Reminder',
    icon: Calendar,
    category: 'Reminders',
    preview: 'Gentle reminder for upcoming services',
    content: {
      subject: 'Reminder: Your appointment is tomorrow',
      preheader: 'Don\'t forget your upcoming appointment',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#f59e0b',
            padding: '30px'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Hi {{customer_name}},\n\nThis is a friendly reminder about your upcoming appointment.',
            align: 'left'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Appointment Details',
            items: [
              'Date: {{appointment_date}}',
              'Time: {{appointment_time}}',
              'Service: {{service_name}}'
            ]
          }
        },
        {
          type: 'button',
          content: {
            text: 'View Appointment',
            url: '#',
            backgroundColor: '#f59e0b',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'feedback',
    name: 'Feedback Request',
    icon: Star,
    category: 'Feedback',
    preview: 'Request customer feedback and reviews',
    content: {
      subject: 'How was your experience?',
      preheader: 'We\'d love to hear from you',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#8b5cf6',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'How Did We Do?',
            subheading: 'Your feedback helps us improve',
            buttonText: 'Leave a Review',
            buttonUrl: '#'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Thank you for choosing {{business_name}}. We\'d love to hear about your recent experience with us.',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    icon: FileText,
    category: 'Updates',
    preview: 'Monthly newsletter template',
    content: {
      subject: 'Monthly Newsletter - {{month}}',
      preheader: 'Latest news and updates',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#0891b2',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'Monthly Update',
            subheading: 'Stay informed with our latest news',
            image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Check out what\'s new this month at {{business_name}}',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'invoice',
    name: 'Invoice',
    icon: FileText,
    category: 'Billing',
    preview: 'Professional invoice template',
    content: {
      subject: 'Invoice #{{invoice_number}}',
      preheader: 'Payment details for your recent service',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#1f2937',
            padding: '30px'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Invoice #{{invoice_number}}',
            align: 'center',
            fontSize: '24px'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Invoice Details',
            items: [
              'Date: {{invoice_date}}',
              'Due Date: {{due_date}}',
              'Amount: {{total_amount}}'
            ]
          }
        },
        {
          type: 'button',
          content: {
            text: 'Pay Now',
            url: '#',
            backgroundColor: '#059669',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'thank-you',
    name: 'Thank You',
    icon: Heart,
    category: 'Customer Care',
    preview: 'Express gratitude to your customers',
    content: {
      subject: 'Thank You for Your Business!',
      preheader: 'We appreciate you',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#ec4899',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'Thank You!',
            subheading: 'We truly appreciate your business',
            backgroundColor: '#fef2f2'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Dear {{customer_name}},\n\nThank you for choosing {{business_name}}. Your trust in us means everything.',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'event',
    name: 'Event Invitation',
    icon: Megaphone,
    category: 'Events',
    preview: 'Invite customers to special events',
    content: {
      subject: 'You\'re Invited: {{event_name}}',
      preheader: 'Join us for a special event',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#7c3aed',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'You\'re Invited!',
            subheading: '{{event_name}}',
            image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800',
            buttonText: 'RSVP Now',
            buttonUrl: '#'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Event Details',
            items: [
              'Date: {{event_date}}',
              'Time: {{event_time}}',
              'Location: {{event_location}}'
            ]
          }
        }
      ]
    }
  },
  {
    id: 'loyalty',
    name: 'Loyalty Program',
    icon: Star,
    category: 'Rewards',
    preview: 'Loyalty program updates and rewards',
    content: {
      subject: 'You\'ve Earned Rewards!',
      preheader: 'Check out your loyalty rewards',
      sections: [
        {
          type: 'header',
          content: {
            logo: true,
            logoInvert: true,
            backgroundColor: '#eab308',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: '🎉 Rewards Unlocked!',
            subheading: 'You\'ve earned {{points}} points',
            buttonText: 'Claim Rewards',
            buttonUrl: '#',
            backgroundColor: '#fef3c7'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Thank you for being a valued member of our loyalty program!',
            align: 'center'
          }
        }
      ]
    }
  }
];

// Template selection modal
function TemplateSelector({ onSelect, onClose }: { onSelect: (template: any) => void; onClose: () => void }) {
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const { selectedBusiness } = useBusiness();
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  const renderTemplatePreview = (template: any) => {
    if (!template || !template.content || !template.content.sections) return null;
    
    return (
      <div className="w-full bg-white rounded-lg overflow-hidden shadow-sm">
        {template.content.sections.map((section: any, index: number) => {
          switch (section.type) {
            case 'header':
              return (
                <div
                  key={index}
                  style={{ 
                    backgroundColor: section.content.backgroundColor || '#6366f1', 
                    padding: '20px'
                  }}
                  className="text-center"
                >
                  {logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt={selectedBusiness?.name || 'Business Logo'} 
                      className="h-8 mx-auto object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  ) : (
                    <div className="text-white font-bold text-lg">{selectedBusiness?.name || 'Your Logo'}</div>
                  )}
                </div>
              );
            case 'hero':
              return (
                <div key={index} style={{ backgroundColor: section.content.backgroundColor || '#f3f4f6' }} className="p-6 text-center">
                  {section.content.image && (
                    <div className="w-full h-32 bg-gray-200 rounded mb-3 flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <h2 className="text-xl font-bold mb-1">{section.content.heading}</h2>
                  <p className="text-sm text-gray-600 mb-3">{section.content.subheading}</p>
                  {section.content.buttonText && (
                    <button className="px-4 py-2 bg-purple-600 text-white rounded text-sm">
                      {section.content.buttonText}
                    </button>
                  )}
                </div>
              );
            case 'text':
              return (
                <div key={index} className="p-4" style={{ textAlign: section.content.align || 'left' }}>
                  <p className="text-sm text-gray-700 line-clamp-2">{section.content.text}</p>
                </div>
              );
            case 'card':
              return (
                <div key={index} className="p-4">
                  <div className="p-3 bg-gray-50 rounded border border-gray-200">
                    <h4 className="font-semibold text-sm mb-1">{section.content.title}</h4>
                    {section.content.items ? (
                      <ul className="text-xs text-gray-600 space-y-1">
                        {section.content.items.slice(0, 2).map((item: string, i: number) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-600">{section.content.content}</p>
                    )}
                  </div>
                </div>
              );
            default:
              return null;
          }
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Choose a Template</h2>
              <p className="text-gray-600 mt-1">Select a pre-designed template or start from scratch</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {/* Blank Template Option */}
            <div
              onClick={() => onSelect(null)}
              onMouseEnter={() => setHoveredTemplate('blank')}
              onMouseLeave={() => setHoveredTemplate(null)}
              className="cursor-pointer"
            >
              <div className="group bg-white border-2 border-dashed border-gray-300 rounded-xl overflow-hidden hover:border-purple-500 transition-all hover:shadow-xl h-[400px] flex flex-col">
                <div className="flex-1 flex flex-col items-center justify-center p-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Plus className="h-10 w-10 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Start Blank</h3>
                  <p className="text-sm text-gray-600 text-center">Create your own design from scratch</p>
                </div>
              </div>
            </div>

            {/* Pre-designed Templates */}
            {emailTemplates.map((template) => {
              const Icon = template.icon;
              const isHovered = hoveredTemplate === template.id;
              
              return (
                <div
                  key={template.id}
                  onClick={() => onSelect(template)}
                  onMouseEnter={() => setHoveredTemplate(template.id)}
                  onMouseLeave={() => setHoveredTemplate(null)}
                  className="cursor-pointer"
                >
                  <div className={`group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all h-[400px] flex flex-col ${
                    isHovered ? 'transform scale-[1.02]' : ''
                  }`}>
                    {/* Template Preview */}
                    <div className="flex-1 bg-gray-50 p-3 overflow-hidden">
                      <div className="transform scale-75 origin-top">
                        {renderTemplatePreview(template)}
                      </div>
                    </div>
                    
                    {/* Template Info */}
                    <div className="p-4 bg-white border-t border-gray-100">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate">{template.name}</h3>
                          <p className="text-xs text-gray-600 line-clamp-2 mt-1">{template.preview}</p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-2">
                            {template.category}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hover Overlay */}
                    {isHovered && (
                      <div className="absolute inset-0 bg-purple-600/10 flex items-center justify-center pointer-events-none">
                        <div className="bg-white rounded-lg shadow-lg px-4 py-2">
                          <span className="text-sm font-semibold text-purple-600">Use This Template</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Email Template Builder
export default function EmailTemplateBuilder({ isOpen, onClose, onSave, initialTemplate }: EmailTemplateBuilderProps) {
  // Show template selector when creating new (no initialTemplate) and hide when editing (has initialTemplate)
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [emailContent, setEmailContent] = useState<any>({
    subject: '',
    preheader: '',
    sections: []
  });
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [isDragging, setIsDragging] = useState(false);
  const [draggedSection, setDraggedSection] = useState<number | null>(null);
  const [dragOverSection, setDragOverSection] = useState<number | null>(null);
  const [showStockPhotos, setShowStockPhotos] = useState(false);
  const [stockPhotoTarget, setStockPhotoTarget] = useState<{ sectionIndex: number; field: string } | null>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [showUploadedPhotos, setShowUploadedPhotos] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  // Mobile-specific states
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showMobileProperties, setShowMobileProperties] = useState(false);
  const { selectedBusiness } = useBusiness();
  const { user, currentResourceId } = useAuth();

  // Initialize based on whether we're creating or editing
  useEffect(() => {
    if (isOpen) {
      if (initialTemplate?.content) {
        // Editing an existing template - don't show selector
        setEmailContent(initialTemplate.content);
        setSelectedTemplate(initialTemplate);
        setTemplateName(initialTemplate.name || '');
        setShowTemplateSelector(false);
      } else {
        // Creating new template - show selector
        setShowTemplateSelector(true);
        setSelectedTemplate(null);
        setEmailContent({
          subject: '',
          preheader: '',
          sections: []
        });
        setTemplateName('');
      }
      
      // Load saved images for the business
      if (selectedBusiness?.id) {
        loadSavedImages();
      }
    }
  }, [isOpen, initialTemplate, selectedBusiness?.id]);

  // Load saved images from database
  const loadSavedImages = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const images = await uploadedImageService.getBusinessImages(selectedBusiness.id);
      // Convert to the format expected by the component
      const formattedImages = images.map(img => ({
        id: img.id,
        url: img.url,
        name: img.name,
        uploadedAt: new Date(img.created_at)
      }));
      setUploadedImages(formattedImages);
    } catch (error) {
      console.error('Error loading saved images:', error);
    }
  };

  // Handle template selection from selector
  useEffect(() => {
    if (selectedTemplate?.content && !initialTemplate) {
      setEmailContent(selectedTemplate.content);
      setTemplateName(selectedTemplate.name || '');
    }
  }, [selectedTemplate]);

  // Section types available to add
  const sectionTypes = [
    { type: 'header', icon: Layout, label: 'Header' },
    { type: 'hero', icon: Image, label: 'Hero Section' },
    { type: 'text', icon: Type, label: 'Text Block' },
    { type: 'image', icon: ImageIcon, label: 'Image' },
    { type: 'button', icon: Square, label: 'Button' },
    { type: 'columns', icon: Columns, label: 'Columns' },
    { type: 'divider', icon: Columns, label: 'Divider' },
    { type: 'card', icon: Square, label: 'Card' },
    { type: 'footer', icon: Layout, label: 'Footer' },
    { type: 'myPhotos', icon: Images, label: 'My Photos', isAction: true }
  ];

  const addSection = (type: string) => {
    // Handle My Photos action
    if (type === 'myPhotos') {
      setShowUploadedPhotos(true);
      return;
    }
    
    const newSection = {
      type,
      content: getDefaultSectionContent(type)
    };
    setEmailContent({
      ...emailContent,
      sections: [...emailContent.sections, newSection]
    });
  };

  const handleImageUpload = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (e: any) => {
      const files = Array.from(e.target.files || []) as File[];
      if (files.length === 0) return;

      setIsUploadingImages(true);
      
      try {
        // Upload images to Supabase Storage and save to database
        const uploadedImgs = await uploadedImageService.uploadMultipleImages(
          files,
          selectedBusiness.id,
          currentResourceId
        );

        if (uploadedImgs.length > 0) {
          // Add to local state for immediate display
          const formattedImages = uploadedImgs.map(img => ({
            id: img.id,
            url: img.url,
            name: img.name,
            uploadedAt: new Date(img.created_at)
          }));
          
          setUploadedImages(prev => [...prev, ...formattedImages]);
          console.log(`Successfully uploaded ${uploadedImgs.length} images`);
        } else {
          console.error('No images were uploaded successfully');
        }
      } catch (error: any) {
        console.error('Error uploading images:', error);
        alert(`Failed to upload images: ${error.message || 'Unknown error'}`);
      } finally {
        setIsUploadingImages(false);
      }
    };
    input.click();
  };

  const getDefaultSectionContent = (type: string) => {
    switch (type) {
      case 'header':
        return {
          showBusinessName: true,
          backgroundColor: '#6366f1',
          textColor: '#ffffff',
          padding: '30px'
        };
      case 'hero':
        return {
          heading: 'Your Heading Here',
          subheading: 'Your subheading text',
          image: '',
          buttonText: 'Call to Action',
          buttonUrl: '#',
          backgroundColor: '#f3f4f6'
        };
      case 'text':
        return {
          text: 'Enter your text content here...',
          align: 'left',
          fontSize: '16px',
          color: '#374151'
        };
      case 'image':
        return {
          url: '',
          alt: 'Image description',
          width: '100%',
          align: 'center'
        };
      case 'button':
        return {
          text: 'Button Text',
          url: '#',
          backgroundColor: '#6366f1',
          color: '#ffffff',
          align: 'center'
        };
      case 'columns':
        return {
          columns: [
            { content: 'Column 1 content' },
            { content: 'Column 2 content' }
          ]
        };
      case 'divider':
        return {
          color: '#e5e7eb',
          thickness: '1px',
          margin: '20px'
        };
      case 'card':
        return {
          title: 'Card Title',
          content: 'Card content goes here',
          backgroundColor: '#f9fafb',
          borderColor: '#e5e7eb'
        };
      case 'footer':
        return {
          text: '© 2024 Your Business. All rights reserved.',
          links: [],
          backgroundColor: '#1f2937',
          color: '#ffffff'
        };
      default:
        return {};
    }
  };

  const updateSection = (index: number, content: any) => {
    const updatedSections = [...emailContent.sections];
    updatedSections[index].content = { ...updatedSections[index].content, ...content };
    setEmailContent({ ...emailContent, sections: updatedSections });
  };

  const deleteSection = (index: number) => {
    const updatedSections = emailContent.sections.filter((_, i) => i !== index);
    setEmailContent({ ...emailContent, sections: updatedSections });
    setSelectedSection(null);
  };

  const duplicateSection = (index: number) => {
    const sectionToDuplicate = emailContent.sections[index];
    const updatedSections = [...emailContent.sections];
    updatedSections.splice(index + 1, 0, { ...sectionToDuplicate });
    setEmailContent({ ...emailContent, sections: updatedSections });
  };

  const moveSection = (fromIndex: number, toIndex: number) => {
    const updatedSections = [...emailContent.sections];
    const [movedSection] = updatedSections.splice(fromIndex, 1);
    updatedSections.splice(toIndex, 0, movedSection);
    setEmailContent({ ...emailContent, sections: updatedSections });
    
    // Update selected section to follow the moved section
    setSelectedSection(toIndex);
  };

  const moveSectionUp = (index: number) => {
    if (index > 0) {
      moveSection(index, index - 1);
    }
  };

  const moveSectionDown = (index: number) => {
    if (index < emailContent.sections.length - 1) {
      moveSection(index, index + 1);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setIsDragging(true);
    setDraggedSection(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSection(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're actually leaving the section
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverSection(null);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedSection !== null && draggedSection !== dropIndex) {
      moveSection(draggedSection, dropIndex);
    }
    
    setIsDragging(false);
    setDraggedSection(null);
    setDragOverSection(null);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedSection(null);
    setDragOverSection(null);
  };

  const handleSave = () => {
    const template = {
      id: initialTemplate?.id, // Include ID if editing
      name: templateName || 'Untitled Template',
      content: emailContent,
      category: selectedTemplate?.category || initialTemplate?.category || 'Custom',
      status: selectedTemplate?.status || initialTemplate?.status || 'draft',
      createdAt: initialTemplate?.createdAt || new Date().toISOString()
    };
    onSave(template);
  };

  const openStockPhotoBrowser = (sectionIndex: number, field: string) => {
    setStockPhotoTarget({ sectionIndex, field });
    setShowStockPhotos(true);
  };

  const handleStockPhotoSelect = (imageUrl: string) => {
    if (stockPhotoTarget) {
      const updatedSections = [...emailContent.sections];
      updatedSections[stockPhotoTarget.sectionIndex].content[stockPhotoTarget.field] = imageUrl;
      setEmailContent({ ...emailContent, sections: updatedSections });
      setStockPhotoTarget(null);
    }
  };

  const openUploadedPhotoBrowser = (sectionIndex: number, field: string) => {
    setStockPhotoTarget({ sectionIndex, field });
    setShowUploadedPhotos(true);
  };

  const handleUploadedPhotoSelect = (imageUrl: string) => {
    if (stockPhotoTarget) {
      const updatedSections = [...emailContent.sections];
      updatedSections[stockPhotoTarget.sectionIndex].content[stockPhotoTarget.field] = imageUrl;
      setEmailContent({
        ...emailContent,
        sections: updatedSections
      });
      setShowUploadedPhotos(false);
      setStockPhotoTarget(null);
    } else {
      // If no target, just close the modal (user is just browsing)
      setShowUploadedPhotos(false);
    }
  };

  const handleDeleteUploadedImage = async (imageId: string) => {
    try {
      // Delete from database and storage
      const success = await uploadedImageService.deleteImage(imageId);
      if (success) {
        // Remove from local state
        setUploadedImages(prev => prev.filter(img => img.id !== imageId));
      } else {
        console.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSection(null);
      // Don't reset template selector or uploaded images - they're handled elsewhere
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (showTemplateSelector) {
    return (
      <TemplateSelector
        onSelect={(template) => {
          setSelectedTemplate(template);
          setShowTemplateSelector(false);
        }}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-100 z-[9999] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
            
            {/* Mobile: Add Section Button */}
            <button
              onClick={() => setShowMobileSidebar(true)}
              className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Add Section"
            >
              <Plus className="h-5 w-5 text-gray-500" />
            </button>
            
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template Name"
                className="text-lg sm:text-xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 rounded px-2 py-1 w-full"
              />
              <p className="text-xs sm:text-sm text-gray-600 px-2 hidden sm:block">{initialTemplate ? 'Editing Template' : 'New Template'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            {/* Mobile: Properties Button */}
            {selectedSection !== null && (
              <button
                onClick={() => setShowMobileProperties(true)}
                className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Section Properties"
              >
                <Settings className="h-5 w-5 text-gray-500" />
              </button>
            )}
            
            {/* Preview Mode Toggle - Hidden on small screens */}
            <div className="hidden sm:flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-white shadow-sm' : ''}`}
              >
                <Monitor className="h-4 w-4 text-gray-600" />
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-white shadow-sm' : ''}`}
              >
                <Smartphone className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            
            <button
              onClick={handleSave}
              className="px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1 sm:space-x-2"
            >
              <Save className="h-4 w-4" />
              <span className="hidden sm:inline">Save Template</span>
              <span className="sm:hidden">Save</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Desktop Left Sidebar - Add Sections */}
        <div className="hidden sm:block w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Add Section</h3>
            <div className="space-y-2">
              {sectionTypes.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.type}
                    onClick={() => addSection(section.type)}
                    disabled={section.disabled}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors ${
                      section.disabled 
                        ? 'opacity-50 cursor-not-allowed bg-gray-50' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${section.disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                    <span className={`text-sm ${section.disabled ? 'text-gray-500' : 'text-gray-700'}`}>
                      {section.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Center - Email Preview/Editor */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-3 sm:p-8">
          <div className={`mx-auto bg-white shadow-lg ${previewMode === 'mobile' ? 'max-w-sm' : 'max-w-2xl'}`}>
            {/* Email Header Fields */}
            <div className="p-3 sm:p-6 border-b border-gray-200">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                  <input
                    type="text"
                    value={emailContent.subject}
                    onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                    placeholder="Enter email subject..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preheader Text</label>
                  <input
                    type="text"
                    value={emailContent.preheader}
                    onChange={(e) => setEmailContent({ ...emailContent, preheader: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                    placeholder="Preview text that appears in inbox..."
                  />
                </div>
              </div>
            </div>

            {/* Email Body Sections */}
            <div className="min-h-[400px]">
              {emailContent.sections.length === 0 ? (
                <div className="p-6 sm:p-12 text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plus className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    <span className="hidden sm:inline">Add sections from the left sidebar to build your email</span>
                    <span className="sm:hidden">Tap the + button above to add sections</span>
                  </p>
                </div>
              ) : (
                emailContent.sections.map((section, index) => (
                  <div
                    key={index}
                    className={`relative group ${selectedSection === index ? 'ring-2 ring-purple-500' : ''} ${
                      dragOverSection === index ? 'ring-2 ring-blue-400 bg-blue-50' : ''
                    } ${draggedSection === index ? 'opacity-50' : ''}`}
                    onClick={() => setSelectedSection(index)}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    {/* Drag Handle */}
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div 
                        className="bg-white rounded-lg shadow-lg p-2 cursor-grab active:cursor-grabbing hover:bg-gray-50"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>

                    {/* Section Controls */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1 bg-white rounded-lg shadow-lg p-1">
                      {/* Move Up Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionUp(index);
                        }}
                        disabled={index === 0}
                        className={`p-1 rounded ${
                          index === 0 
                            ? 'opacity-30 cursor-not-allowed' 
                            : 'hover:bg-gray-100'
                        }`}
                        title="Move Up"
                      >
                        <ChevronUp className="h-3 w-3 text-gray-600" />
                      </button>
                      
                      {/* Move Down Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionDown(index);
                        }}
                        disabled={index === emailContent.sections.length - 1}
                        className={`p-1 rounded ${
                          index === emailContent.sections.length - 1 
                            ? 'opacity-30 cursor-not-allowed' 
                            : 'hover:bg-gray-100'
                        }`}
                        title="Move Down"
                      >
                        <ChevronDown className="h-3 w-3 text-gray-600" />
                      </button>
                      
                      {/* Duplicate Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateSection(index);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Duplicate Section"
                      >
                        <Copy className="h-3 w-3 text-gray-600" />
                      </button>
                      
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSection(index);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Delete Section"
                      >
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </button>
                    </div>

                    {/* Render Section Content */}
                    <SectionRenderer
                      section={section}
                      onChange={(content) => updateSection(index, content)}
                      isSelected={selectedSection === index}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Desktop Right Sidebar - Properties Panel */}
        {selectedSection !== null && (
          <div className="hidden sm:block w-80 bg-white border-l border-gray-200 overflow-y-auto">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Section Properties</h3>
              <PropertiesPanel
                section={emailContent.sections[selectedSection]}
                sectionIndex={selectedSection}
                onChange={(content) => updateSection(selectedSection, content)}
                onOpenStockPhotos={openStockPhotoBrowser}
                onOpenUploadedPhotos={openUploadedPhotoBrowser}
                hasUploadedImages={uploadedImages.length > 0}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Add Section Modal */}
      {showMobileSidebar && (
        <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-end">
          <div className="bg-white w-full max-h-[80vh] rounded-t-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Section</h3>
              <button
                onClick={() => setShowMobileSidebar(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="grid grid-cols-2 gap-3">
                {sectionTypes.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.type}
                      onClick={() => {
                        addSection(section.type);
                        setShowMobileSidebar(false);
                      }}
                      disabled={section.disabled}
                      className={`flex flex-col items-center space-y-2 p-4 text-center rounded-lg transition-colors ${
                        section.disabled 
                          ? 'opacity-50 cursor-not-allowed bg-gray-50' 
                          : 'hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <Icon className={`h-6 w-6 ${section.disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${section.disabled ? 'text-gray-500' : 'text-gray-700'}`}>
                        {section.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Properties Modal */}
      {showMobileProperties && selectedSection !== null && (
        <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-end">
          <div className="bg-white w-full max-h-[90vh] rounded-t-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Section Properties</h3>
              <button
                onClick={() => setShowMobileProperties(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              <PropertiesPanel
                section={emailContent.sections[selectedSection]}
                sectionIndex={selectedSection}
                onChange={(content) => updateSection(selectedSection, content)}
                onOpenStockPhotos={openStockPhotoBrowser}
                onOpenUploadedPhotos={openUploadedPhotoBrowser}
                hasUploadedImages={uploadedImages.length > 0}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stock Photo Browser Modal */}
      <StockPhotoBrowser
        isOpen={showStockPhotos}
        onClose={() => {
          setShowStockPhotos(false);
          setStockPhotoTarget(null);
        }}
        onSelectImage={handleStockPhotoSelect}
      />

      <UploadedPhotosBrowser
        isOpen={showUploadedPhotos}
        onClose={() => {
          setShowUploadedPhotos(false);
          setStockPhotoTarget(null);
        }}
        onSelectImage={handleUploadedPhotoSelect}
        uploadedImages={uploadedImages}
        onDeleteImage={handleDeleteUploadedImage}
        onUploadImages={handleImageUpload}
        isUploading={isUploadingImages}
        isSelectionMode={!!stockPhotoTarget}
      />
    </div>
  );
}

// Section Renderer Component
function SectionRenderer({ section, onChange, isSelected }: any) {
  const { selectedBusiness } = useBusiness();
  const { type, content } = section;
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  switch (type) {
    case 'header':
      return (
        <div
          style={{ 
            backgroundColor: content.backgroundColor, 
            padding: content.padding,
            color: content.textColor || '#ffffff'
          }}
          className="text-center"
        >
          {content.showBusinessName !== false && (
            <div 
              className="font-bold text-2xl uppercase tracking-wider"
              style={{ color: content.textColor || '#ffffff' }}
            >
              {selectedBusiness?.name || 'Your Business Name'}
            </div>
          )}
        </div>
      );

    case 'hero':
      return (
        <div style={{ backgroundColor: content.backgroundColor }} className="p-8 text-center">
          {content.image && (
            <img src={content.image} alt="Hero" className="w-full mb-4 rounded-lg" />
          )}
          <h1 className="text-3xl font-bold mb-2">{content.heading}</h1>
          <p className="text-lg text-gray-600 mb-4">{content.subheading}</p>
          {content.buttonText && (
            <button className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium">
              {content.buttonText}
            </button>
          )}
        </div>
      );

    case 'text':
      return (
        <div className="p-6" style={{ textAlign: content.align }}>
          <p style={{ fontSize: content.fontSize, color: content.color }}>
            {content.text}
          </p>
        </div>
      );

    case 'button':
      return (
        <div className="p-6" style={{ textAlign: content.align }}>
          <button
            style={{ backgroundColor: content.backgroundColor, color: content.color }}
            className="px-6 py-3 rounded-lg font-medium"
          >
            {content.text}
          </button>
        </div>
      );

    case 'divider':
      return (
        <div style={{ padding: content.margin }}>
          <hr style={{ borderColor: content.color, borderWidth: content.thickness }} />
        </div>
      );

    case 'card':
      return (
        <div className="p-6">
          <div
            style={{ backgroundColor: content.backgroundColor, borderColor: content.borderColor }}
            className="p-4 rounded-lg border"
          >
            <h3 className="font-semibold mb-2">{content.title}</h3>
            <p className="text-gray-600">{content.content}</p>
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="p-6" style={{ textAlign: content.align || 'center' }}>
          {content.url ? (
            <img
              src={content.url}
              alt={content.alt || 'Image'}
              style={{ width: content.width || '100%' }}
              className="mx-auto rounded-lg"
            />
          ) : (
            <div className="bg-gray-100 rounded-lg p-12 text-center">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Click to add an image</p>
            </div>
          )}
        </div>
      );

    case 'footer':
      return (
        <div
          style={{ backgroundColor: content.backgroundColor, color: content.color }}
          className="p-6 text-center text-sm"
        >
          {content.text}
        </div>
      );

    default:
      return null;
  }
}

// Properties Panel Component
function PropertiesPanel({ section, sectionIndex, onChange, onOpenStockPhotos, onOpenUploadedPhotos, hasUploadedImages }: any) {
  const { selectedBusiness } = useBusiness();
  const { type, content } = section;
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  switch (type) {
    case 'header':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Show Business Name</label>
            <button
              onClick={() => onChange({ showBusinessName: !content.showBusinessName })}
              className={`w-full px-3 py-2 rounded-lg border ${
                content.showBusinessName !== false ? 'bg-purple-100 border-purple-300' : 'bg-gray-100 border-gray-300'
              }`}
            >
              {content.showBusinessName !== false ? 'Business Name Visible' : 'Business Name Hidden'}
            </button>
          </div>
          

          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
            <input
              type="color"
              value={content.backgroundColor}
              onChange={(e) => onChange({ backgroundColor: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Padding</label>
            <select
              value={content.padding}
              onChange={(e) => onChange({ padding: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="20px">Small</option>
              <option value="30px">Medium</option>
              <option value="40px">Large</option>
              <option value="50px">Extra Large</option>
            </select>
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text Content</label>
            <textarea
              value={content.text}
              onChange={(e) => onChange({ text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows={4}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
            <div className="flex space-x-2">
              <button
                onClick={() => onChange({ align: 'left' })}
                className={`p-2 rounded ${content.align === 'left' ? 'bg-purple-100' : 'bg-gray-100'}`}
              >
                <AlignLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => onChange({ align: 'center' })}
                className={`p-2 rounded ${content.align === 'center' ? 'bg-purple-100' : 'bg-gray-100'}`}
              >
                <AlignCenter className="h-4 w-4" />
              </button>
              <button
                onClick={() => onChange({ align: 'right' })}
                className={`p-2 rounded ${content.align === 'right' ? 'bg-purple-100' : 'bg-gray-100'}`}
              >
                <AlignRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
            <input
              type="text"
              value={content.fontSize}
              onChange={(e) => onChange({ fontSize: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
            <input
              type="color"
              value={content.color}
              onChange={(e) => onChange({ color: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      );

    case 'hero':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
            <input
              type="text"
              value={content.heading}
              onChange={(e) => onChange({ heading: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subheading</label>
            <input
              type="text"
              value={content.subheading}
              onChange={(e) => onChange({ subheading: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
            <div className="space-y-2">
              <input
                type="text"
                value={content.image || ''}
                onChange={(e) => onChange({ image: e.target.value })}
                placeholder="Enter image URL..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={() => onOpenStockPhotos(sectionIndex, 'image')}
                className="w-full px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center justify-center space-x-2"
              >
                <Images className="h-4 w-4" />
                <span>Browse Stock Photos</span>
              </button>
              {hasUploadedImages && (
                <button
                  onClick={() => onOpenUploadedPhotos(sectionIndex, 'image')}
                  className="w-full px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center space-x-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  <span>Select from My Photos</span>
                </button>
              )}
              {content.image && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <img src={content.image} alt="Preview" className="w-full h-24 object-cover rounded" />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input
              type="text"
              value={content.buttonText}
              onChange={(e) => onChange({ buttonText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button URL</label>
            <input
              type="text"
              value={content.buttonUrl}
              onChange={(e) => onChange({ buttonUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      );

    case 'button':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input
              type="text"
              value={content.text}
              onChange={(e) => onChange({ text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button URL</label>
            <input
              type="text"
              value={content.url}
              onChange={(e) => onChange({ url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
            <input
              type="color"
              value={content.backgroundColor}
              onChange={(e) => onChange({ backgroundColor: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
            <input
              type="color"
              value={content.color}
              onChange={(e) => onChange({ color: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
            <div className="space-y-2">
              <input
                type="text"
                value={content.url || ''}
                onChange={(e) => onChange({ url: e.target.value })}
                placeholder="Enter image URL..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={() => onOpenStockPhotos(sectionIndex, 'url')}
                className="w-full px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center justify-center space-x-2"
              >
                <Images className="h-4 w-4" />
                <span>Browse Stock Photos</span>
              </button>
              {hasUploadedImages && (
                <button
                  onClick={() => onOpenUploadedPhotos(sectionIndex, 'url')}
                  className="w-full px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center space-x-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  <span>Select from My Photos</span>
                </button>
              )}
              {content.url && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <img src={content.url} alt="Preview" className="w-full h-24 object-cover rounded" />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
            <input
              type="text"
              value={content.alt || ''}
              onChange={(e) => onChange({ alt: e.target.value })}
              placeholder="Describe the image..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
            <input
              type="text"
              value={content.width || '100%'}
              onChange={(e) => onChange({ width: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
            <select
              value={content.align || 'center'}
              onChange={(e) => onChange({ align: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </div>
      );

    default:
      return <div className="text-sm text-gray-500">Select a section to edit its properties</div>;
  }
}
